import { IconChevronDown, IconCircleFilled } from '@tabler/icons-react';
import {
  Burger,
  Center,
  Container,
  Group,
  Menu,
  Drawer,
  Stack,
  Text,
  Box,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import classes from './UsersMenu.module.css';
import { useState } from 'react';
import { LogoSVG } from '@components/Logo/Logo';
import { useIntl } from 'react-intl';
import { ThemeSwitch, useTheme } from '@components/ThemeSwitch';
import { LanguageSwitch } from '@components/LanguageSwitch';
import { useUser } from '@contexts/UserContext';
import {
  forceReconnect,
  getQueueHealth,
  makeApiCall,
  QueueStats,
  resetLockedFields,
  resetQueues,
  restartConsumer,
  startMultipleConsumers,
  triggerLoop,
} from '../../helpers/queueManagementDropdown';
import { useLoading } from '@contexts/LoadingContext';
import { useWebSocket } from '@/hooks';
import { NotificationCenter } from '@/users/components/NotificationCenter';
import { getAppVersion } from '@commons/versionManager';
import { useAppDispatch } from '@store';
import { updateFileFromWebSocket } from '@store/slices/foldersSlice';
import { addNotification } from '@store/slices/notificationsSlice';
import { SettingsInitializer } from '@/components/SettingsInitializer';
import { playNotificationSound } from '@/commons/notificationSound';
import { useNavigationLinks } from './hooks/useNavigationLinks';
import { QueueStatsModal } from './components/QueueStatsModal';
import { ConfirmActionModal } from './components/ConfirmActionModal';
import { DesktopMenu } from './components/DesktopMenu';
import { MobileMenu } from './components/MobileMenu';
import { Notification as NotificationType } from '@/users/components/NotificationCenter/types';

export function UsersMenu() {
  const intl = useIntl();
  const appVersion = getAppVersion();
  const { computedColorScheme } = useTheme();
  const { setIsLoading } = useLoading();
  const { user, logout } = useUser();
  const username = user?.username || '';
  const dispatch = useAppDispatch();

  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    action: () => void;
    title: string;
    message: string;
  } | null>(null);

  // Initialize WebSocket connection for real-time updates
  const token = localStorage.getItem('userToken') || '';
  const { connected } = useWebSocket({
    token,
    autoConnect: !!token && !!user,
    onNotification: (notification: NotificationType) => {
      playNotificationSound();

      // Add notification to store without payload property
      const { payload, ...notificationWithoutPayload } = notification;
      dispatch(addNotification(notificationWithoutPayload));

      // Update Redux store if it's a file update
      if (
        notification.type === 'update' &&
        payload?.resource === 'file' &&
        payload?.resourceId &&
        payload?.file
      ) {
        dispatch(
          updateFileFromWebSocket({
            fileId: payload.resourceId,
            fileData: payload.file,
          })
        );
      }
    },
  });

  const getQueueStatsHandler = async () => {
    try {
      setIsLoading(true);
      const data = await makeApiCall('/folders/patch/stats');
      setQueueStats(data.data);
      setShowStats(true);
    } catch (error) {
      console.error('Failed to get queue stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const links = useNavigationLinks({
    username,
    onGetQueueStats: getQueueStatsHandler,
    onGetQueueHealth: () =>
      getQueueHealth(
        () => setIsLoading(true),
        () => setIsLoading(false)
      ),
    onRestartConsumer: () =>
      setConfirmAction({
        action: () =>
          restartConsumer(
            () => setIsLoading(true),
            () => setIsLoading(false)
          ),
        title: intl.formatMessage({ id: 'queue.restartConsumer' }),
        message: intl.formatMessage({
          id: 'queue.confirmRestartConsumer',
        }),
      }),
    onStartMultipleConsumers: () =>
      setConfirmAction({
        action: () =>
          startMultipleConsumers(
            () => setIsLoading(true),
            () => setIsLoading(false)
          ),
        title: intl.formatMessage({ id: 'queue.startMultipleConsumers' }),
        message: intl.formatMessage({
          id: 'queue.confirmStartMultipleConsumers',
        }),
      }),
    onForceReconnect: () =>
      setConfirmAction({
        action: () =>
          forceReconnect(
            () => setIsLoading(true),
            () => setIsLoading(false)
          ),
        title: intl.formatMessage({ id: 'queue.forceReconnect' }),
        message: intl.formatMessage({
          id: 'queue.confirmForceReconnect',
        }),
      }),
    onResetQueues: () =>
      setConfirmAction({
        action: () =>
          resetQueues(
            () => setIsLoading(true),
            () => setIsLoading(false)
          ),
        title: intl.formatMessage({ id: 'queue.resetQueues' }),
        message: intl.formatMessage({ id: 'queue.confirmResetQueues' }),
      }),
    onResetLockedFields: () =>
      setConfirmAction({
        action: () =>
          resetLockedFields(
            () => setIsLoading(true),
            () => setIsLoading(false)
          ),
        title: intl.formatMessage({ id: 'queue.resetLockedFields' }),
        message: intl.formatMessage({
          id: 'queue.confirmResetLockedFields',
        }),
      }),
    onTriggerLoop: () =>
      triggerLoop(
        () => setIsLoading(true),
        () => setIsLoading(false)
      ),
  });

  const [opened, { toggle, close }] = useDisclosure(false);

  if (!user) {
    return null;
  }

  return (
    <>
      <SettingsInitializer />
      <header className={classes.header}>
        <Container size="xl">
          <div className={classes.inner}>
            <Group gap="md">
              <LogoSVG height={28} theme={computedColorScheme} />
              <Group gap={5} visibleFrom="sm">
                <DesktopMenu links={links} />
              </Group>
            </Group>
            <Group gap="xs">
              {user && (
                <>
                  {/* Desktop: Menu with dropdown */}
                  <Box visibleFrom="sm">
                    <Menu
                      trigger="hover"
                      transitionProps={{ exitDuration: 0 }}
                      withinPortal
                      width="target"
                    >
                      <Menu.Target>
                        <button
                          type="button"
                          className={classes.link}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          <Center>
                            <IconCircleFilled
                              size={8}
                              color={connected ? 'green' : 'red'}
                              style={{ marginRight: '6px' }}
                            />
                            <span className={classes.linkLabel}>
                              {user.username}
                            </span>
                            <IconChevronDown size={14} stroke={1.5} />
                          </Center>
                        </button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={logout}>
                          {intl.formatMessage({ id: 'logout' })}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Box>
                  {/* Mobile: Just username and status (no dropdown) */}
                  <Box hiddenFrom="sm">
                    <Center>
                      <IconCircleFilled
                        size={8}
                        color={connected ? 'green' : 'red'}
                        style={{ marginRight: '6px' }}
                      />
                      <span className={classes.linkLabel}>{user.username}</span>
                    </Center>
                  </Box>
                </>
              )}
              <Text size="sm" c="dimmed">
                v{appVersion}
              </Text>
              <NotificationCenter />
              <LanguageSwitch />
              <ThemeSwitch />
              <Burger
                opened={opened}
                onClick={toggle}
                size="sm"
                hiddenFrom="sm"
              />
            </Group>
          </div>
        </Container>

        <Drawer
          opened={opened}
          onClose={close}
          size="100%"
          padding="md"
          title={<LogoSVG height={28} theme={computedColorScheme} />}
          hiddenFrom="sm"
          zIndex={1000000}
        >
          <Stack gap="sm">
            {user && (
              <Stack gap="xs" mb="xs">
                <Group gap="xs">
                  <IconCircleFilled
                    size={8}
                    color={connected ? 'green' : 'red'}
                  />
                  <Text size="sm" fw={500}>
                    {user.username}
                  </Text>
                </Group>
                <Text
                  size="xs"
                  c="dimmed"
                  onClick={logout}
                  style={{ cursor: 'pointer' }}
                >
                  {intl.formatMessage({ id: 'logout' })}
                </Text>
              </Stack>
            )}
            <MobileMenu links={links} onClose={close} />
          </Stack>
        </Drawer>
      </header>

      {/* Queue Stats Modal */}
      <QueueStatsModal
        opened={showStats}
        onClose={() => setShowStats(false)}
        stats={queueStats}
      />

      {/* Confirm Action Modal */}
      {confirmAction && (
        <ConfirmActionModal
          opened={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.action}
          title={confirmAction.title}
          message={confirmAction.message}
        />
      )}
    </>
  );
}
